import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import SpotlightTour from "./SpotlightTour";

// Small fixed pill at the bottom of the page that replays the tutorial, mirroring
// the "How it works" button used in Smart Search / Sequence Search.
const HowItWorksButton = styled.button`
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 100030;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  background: ${(props) => props.theme.sidebar};
  border: 0.5px solid ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.green};
  padding: 8px 18px;
  border-radius: 999px;
  font-family: "SatoshiBold";
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.green};
    color: ${(props) => props.theme.black};
    opacity: 1;
  }
`;

/**
 * Self-contained tutorial launcher: shows a section-by-section spotlight tour on
 * first visit (persisted under `storageKey`) and a "How it works" button to
 * replay it. The tour resolves its targets by `data-tour` attributes, so this
 * component needs nothing from the page beyond being rendered once. Reused by the
 * events and video pages — pass each page's own `steps` and `storageKey`.
 */
const TutorialLauncher = ({ steps, storageKey, enabled = true }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }
    if (!localStorage.getItem(storageKey)) {
      const openTimer = window.setTimeout(() => {
        setOpen(true);
      }, 500);

      return () => {
        window.clearTimeout(openTimer);
      };
    }
  }, [enabled, storageKey]);

  const dismiss = useCallback(() => {
    setOpen(false);
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, "1");
    }
  }, [storageKey]);

  if (!enabled) {
    return null;
  }

  return (
    <>
      {!open && (
        <HowItWorksButton type="button" onClick={() => setOpen(true)}>
          {t("tutorial.help")}
        </HowItWorksButton>
      )}
      <SpotlightTour steps={steps} open={open} onClose={dismiss} />
    </>
  );
};

export default TutorialLauncher;
